import { DisplayMeasurementProfileDetail } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { LineChartPro, MarkElement, MarkElementProps } from "@mui/x-charts-pro";
import { round } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { useStatusDataByDeviceId } from "@/hooks/statusData/useStatusDataByDeviceId";
import i18n from "@/i18n";
import ColorInterval from "@/models/ColorInterval";
import { safeLocale } from "@/utilities/i18n/safeLocale";

interface LineChartProps {
  diagnosticId: string;
  deviceId: string;
  diagnosticUnit?: DisplayMeasurementProfileDetail;
  // To show more than 24h
  hours?: number;
}

const slotsMark = (props: MarkElementProps) => {
  // @ts-ignore
  return <MarkElement sx={{ scale: 0.5, fill: props.color }} {...props} />;
};

// Function to populate thresholds and colors based on lt and gt
const populateThresholdsAndColors = (
  colorIntervals: ColorInterval[],
): { thresholds: number[]; colors: string[] } => {
  const thresholds: number[] = [];
  const colors: string[] = ["black"];

  for (const colorInterval of colorIntervals) {
    thresholds.push(colorInterval.min);
    thresholds.push(colorInterval.max);

    colors.push(colorInterval.color);
    colors.push("black");
  }

  return { thresholds, colors };
};

export const LastNhDiagnosticLineChart: React.FC<LineChartProps> = ({
  diagnosticId,
  deviceId,
  diagnosticUnit,
  hours = 25,
}) => {
  const { data: data = [], isLoading } = useStatusDataByDeviceId(
    diagnosticId,
    deviceId,
    new Date(new Date().getTime() - hours * 60 * 60 * 1000),
  );

  const { colorIntervals } = useDataGridSettings();

  const { thresholds, colors } = populateThresholdsAndColors(
    diagnosticId in colorIntervals ? colorIntervals[diagnosticId] : [],
  );
  const { t } = useTranslation();
  if (!isLoading && (!data || data.length <= 1)) {
    return (
      <Box
        sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
      >
        <Typography>{t("modal.notEnoughData")}</Typography>
      </Box>
    );
  }
  const unitFactor = diagnosticUnit?.displayUnitOfMeasure?.factorFromBase || 1;
  const { min, max } = data.reduce(
    (acc, obj) => ({
      min: obj.data * unitFactor < acc.min ? obj.data * unitFactor : acc.min,
      max: obj.data * unitFactor > acc.max ? obj.data * unitFactor : acc.max,
    }),
    { min: Infinity, max: -Infinity },
  );

  const tickFormatter = (value: number, context: any) => {
    const date = new Date(value);

    if (context.location !== "tick") {
      // Tooltip / Hover: volles Datum + Uhrzeit
      return date.toLocaleString(safeLocale(i18n.language), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    }

    if (hours <= 25) {
      // kurze Zeiträume: nur Uhrzeit
      return date.toLocaleTimeString(safeLocale(i18n.language), {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    // längere Zeiträume: nur Datum
    return date.toLocaleDateString(safeLocale(i18n.language), {
      day: "2-digit",
      month: "2-digit",
    });
  };

  return (
    <LineChartPro
      margin={{ left: 80 }}
      dataset={data?.map(d => {
        return {
          data: round(d.data) * unitFactor,
          dateTime: d.dateTime,
        };
      })}
      experimentalMarkRendering={true}
      series={[
        {
          dataKey: "data",
          showMark: false,
          curve: "linear",
          color: "black",
          valueFormatter: (value: number | null) =>
            value !== null ? `${value} ${diagnosticUnit?.displayUnitOfMeasure?.name || ""}` : "",
        },
      ]}
      xAxis={[
        {
          tickNumber: 5,
          dataKey: "dateTime",
          scaleType: "linear",
          valueFormatter: (value: number, context) => tickFormatter(value, context),
          max: new Date().getTime(),
          zoom: {
            minStart: 0,
            maxEnd: 100,
            minSpan: 1,
            maxSpan: 101,
            step: 5,
            panning: true,
          },
        },
      ]}
      yAxis={[
        {
          ...(thresholds?.length > 0
            ? {
                colorMap: {
                  type: "piecewise",
                  thresholds: thresholds,
                  colors: colors,
                },
              }
            : {}),
          valueFormatter: (value: number | null) => {
            return value !== null
              ? `${value} ${diagnosticUnit?.displayUnitOfMeasure?.name || ""}`
              : "";
          },

          min: min !== -Infinity ? min - 1 : undefined,
          max: max !== Infinity ? max + 1 : undefined,
        },
      ]}
      grid={{ vertical: false, horizontal: true }}
      loading={isLoading}
      slots={{
        mark: slotsMark,
        noDataOverlay: () => <div>{t("modal.notEnoughData")}</div>,
      }}
    ></LineChartPro>
  );
};
